// A history entry made by navigateTo remembers the page it came from, so the
// header can offer "Back" only when Back really returns to a PremiereLens page.
// document.referrer doesn't change on client-side navigation, so history state carries it.
const PREVIOUS_PAGE_KEY = "premierelensPreviousPage";

export function getPreviousPageTitle() {
  if (typeof window === "undefined") return null;
  const title = window.history.state?.[PREVIOUS_PAGE_KEY]?.title;
  return typeof title === "string" && title ? title : null;
}

// Direct arrivals get a real link to the page's parent; in-app arrivals get Back.
export function resolveBackLink({ parentHref = "/", parentLabel = "Home", previousTitle = null } = {}) {
  if (previousTitle) {
    return { inApp: true, href: parentHref, text: "Back", name: `Back to ${previousTitle}` };
  }
  return { inApp: false, href: parentHref, text: parentLabel, name: parentLabel };
}

// The new page may render after the history change, so wait a few frames for the target.
function scrollToHash(hash) {
  const id = hash.slice(1);
  if (!id) return;
  let frames = 0;
  const tryScroll = () => {
    const target = document.getElementById(id);
    if (target) {
      target.scrollIntoView();
    } else if (frames++ < 30) {
      window.requestAnimationFrame(tryScroll);
    }
  };
  window.requestAnimationFrame(tryScroll);
}

export function navigateTo(href, { replace = false } = {}) {
  if (typeof window === "undefined" || !href) {
    return;
  }

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin === window.location.origin) {
      const nextPath = `${url.pathname}${url.search}${url.hash}`;
      if (replace) {
        // The entry before this one is unchanged, so keep what it recorded.
        window.history.replaceState(window.history.state, "", nextPath);
      } else {
        const title = document.title.replace(/\s*\|\s*PremiereLens$/, "").trim();
        window.history.pushState({ [PREVIOUS_PAGE_KEY]: { title } }, "", nextPath);
      }
      window.dispatchEvent(new PopStateEvent("popstate"));
      window.scrollTo(0, 0);
      scrollToHash(url.hash);
      return;
    }
  } catch {
    // Fall through to hard navigation.
  }

  if (replace) {
    window.location.replace(href);
  } else {
    window.location.href = href;
  }
}

export function handleInternalLinkClick(event, href) {
  if (!event || !href) {
    return;
  }

  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.altKey ||
    event.ctrlKey ||
    event.shiftKey
  ) {
    return;
  }

  event.preventDefault();
  navigateTo(href);
}
